import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { QrCode, Keyboard, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

// Parse legacy Wineemotion QR URLs (?QR=...&D=<yyMMdd>&N=<progressive>) or raw serials
function parseScannedText(text) {
  if (!text) return null;
  const trimmed = text.trim();
  try {
    const url = new URL(trimmed);
    const D = url.searchParams.get("D");
    const N = url.searchParams.get("N");
    if (D && N) return { serial: `${D}${N}` };
    const s = url.searchParams.get("serial");
    if (s) return { serial: s };
  } catch (_) {
    // not a URL, keep as raw
  }
  // Wineemotion serials are alphanumeric — accept any non-empty scan
  if (trimmed.length >= 4 && trimmed.length <= 40) {
    return { serial: trimmed.replace(/[^A-Za-z0-9]/g, "") };
  }
  return null;
}

export function QrScannerDialog({ open, onOpenChange, onSerialDetected }) {
  const containerRef = useRef(null);
  const scannerRef = useRef(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);
  const [manualSerial, setManualSerial] = useState("");
  const nav = useNavigate();

  const stopScanner = async () => {
    if (!scannerRef.current) return;
    try { await scannerRef.current.stop(); } catch (_) { /* ignore */ }
    try { scannerRef.current.clear(); } catch (_) { /* ignore */ }
    scannerRef.current = null;
  };

  const handleDecoded = (text) => {
    const parsed = parseScannedText(text);
    if (!parsed?.serial) {
      toast.error("QR non riconosciuto");
      return;
    }
    stopScanner().finally(() => {
      toast.success(`Seriale rilevato: ${parsed.serial}`);
      onOpenChange(false);
      if (onSerialDetected) onSerialDetected(parsed.serial);
      else nav(`/r/${parsed.serial}`);
    });
  };

  useEffect(() => {
    if (!open) {
      // stop any running scanner
      stopScanner();
      setError(null);
      setManualSerial("");
      return;
    }
    let cancelled = false;
    setStarting(true);
    setError(null);
    // Dynamic import to keep initial bundle light
    import("html5-qrcode").then(({ Html5Qrcode }) => {
      if (cancelled || !containerRef.current) return;
      const scanner = new Html5Qrcode("qr-scanner-region");
      scannerRef.current = scanner;
      scanner
        .start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 240, height: 240 } },
          (decodedText) => handleDecoded(decodedText),
          () => {}, // ignore per-frame decode errors
        )
        .catch((err) => {
          if (cancelled) return;
          setError(err?.message || "Impossibile accedere alla fotocamera. Verifica i permessi del browser.");
        })
        .finally(() => setStarting(false));
    }).catch((err) => {
      setError(err?.message || "Errore nel caricamento del modulo scanner");
      setStarting(false);
    });
    return () => { cancelled = true; };
  }, [open]);

  const submitManual = () => {
    const s = manualSerial.trim().replace(/[^A-Za-z0-9]/g, "");
    if (!s) { toast.error("Inserisci un seriale"); return; }
    stopScanner().finally(() => {
      onOpenChange(false);
      if (onSerialDetected) onSerialDetected(s);
      else nav(`/r/${s}`);
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-md" data-testid="qr-scanner-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight flex items-center gap-2">
            <QrCode className="h-5 w-5 text-brand" /> Scansiona QR dispenser
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            Inquadra il QR sul dispenser oppure inserisci il seriale manualmente.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="rounded-sm border border-zinc-200 bg-zinc-950 aspect-square overflow-hidden relative">
            <div id="qr-scanner-region" ref={containerRef} className="w-full h-full [&_video]:!w-full [&_video]:!h-full [&_video]:object-cover" />
            {starting && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-white gap-2">
                <Loader2 className="h-8 w-8 animate-spin" />
                <div className="text-xs uppercase tracking-widest">Avvio fotocamera...</div>
              </div>
            )}
            {error && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-white gap-2 p-6 text-center">
                <AlertTriangle className="h-8 w-8 text-amber-400" />
                <div className="text-xs">{error}</div>
              </div>
            )}
          </div>

          <div className="pt-2 border-t border-zinc-200">
            <Label className="text-xs uppercase tracking-widest text-zinc-500 flex items-center gap-1">
              <Keyboard className="h-3 w-3" /> Oppure inserisci il seriale
            </Label>
            <div className="flex gap-2 mt-1">
              <Input
                data-testid="qr-manual-input"
                value={manualSerial}
                onChange={(e) => setManualSerial(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && submitManual()}
                placeholder="es. 220621000000"
                className="rounded-sm font-mono"
                autoComplete="off"
                autoCapitalize="off"
              />
              <Button data-testid="qr-manual-submit" onClick={submitManual} className="bg-brand hover:bg-brand-dark text-white rounded-sm shrink-0">
                Apri
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
